import { MediaMatcher } from '@angular/cdk/layout';
import {
  computed,
  DOCUMENT,
  effect,
  inject,
  Service,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { fromEvent, map } from 'rxjs';
import { OnyxTheme, OnyxThemePreference } from '../enums';
import { UtilityHelper } from '../helpers';
import { OnyxStorageKey } from '../internal/enums';

@Service()
export class OnyxThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly darkQuery = inject(MediaMatcher).matchMedia(
    '(prefers-color-scheme: dark)',
  );

  private isSystemDark = toSignal(
    fromEvent<MediaQueryListEvent>(this.darkQuery, 'change').pipe(
      map(({ matches }) => matches),
    ),
    { initialValue: this.darkQuery.matches },
  );

  private _preference = signal(this.readPreference());
  public get preference() {
    return this._preference.asReadonly();
  }

  public theme = computed(() => {
    const preference = this._preference();
    if (preference === OnyxThemePreference.SYSTEM) {
      return this.isSystemDark() ? OnyxTheme.DARK : OnyxTheme.LIGHT;
    }

    return preference === OnyxThemePreference.DARK
      ? OnyxTheme.DARK
      : OnyxTheme.LIGHT;
  });

  constructor() {
    effect(() =>
      this.document.documentElement.setAttribute('data-theme', this.theme()),
    );
  }

  public setPreference(preference: OnyxThemePreference): void {
    this._preference.set(preference);
    try {
      localStorage.setItem(OnyxStorageKey.THEME, preference);
    } catch {
      // Blocked storage: the theme still applies for this session
    }
  }

  private readPreference(): OnyxThemePreference {
    try {
      const saved = localStorage.getItem(OnyxStorageKey.THEME);
      return UtilityHelper.isEnum(OnyxThemePreference, saved)
        ? saved
        : OnyxThemePreference.SYSTEM;
    } catch {
      return OnyxThemePreference.SYSTEM;
    }
  }
}
